/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, useRef, useState } from 'react';
import { Modal, Input, message } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import { useMutation, queryCache } from 'react-query';

import { ThemedStyleFn } from '~src/hooks/useStyles';
import { DbFolder } from '~src/types/db';
import { appLog } from '~src/utils/log';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { useProjectId } from '~src/hooks/useProjectId';
import {
  createGroupLabel,
  RegistrationLabel,
  Label,
  editGroupLabel,
} from '~src/services/api/labels';
import { useRegistrationLabelGroups } from '~src/hooks/api-hooks/useRegistrationLabelGroups';

export interface CreateEditTagModalProps extends Partial<ModalProps> {
  parent?: DbFolder;
  group: RegistrationLabel | null;
  label?: Label | null;
}

export const CreateEditLabelModal: React.FC<CreateEditTagModalProps> = ({
  parent,
  onCancel,
  group,
  label,
  ...modalProps
}) => {
  const intl = useIntl();
  const projectId = useProjectId();
  const [nameVal, setNameVal] = useState(label?.title ?? '');
  const nameInputRef = useRef<Input>(null);

  const registrationLabelGroups = useRegistrationLabelGroups(false);
  const labelsQuery = registrationLabelGroups.data;

  useEffect(() => {
    setNameVal(label?.title ?? '');
  }, [label]);

  const [doCreateLabel, { isLoading }] = useMutation(createGroupLabel, {
    onSuccess: (label) => {
      // hide modal
      // @ts-ignore-line
      onCancel && onCancel();

      queryCache.invalidateQueries('registrationLabelGroups');

      // reset values
      setNameVal('');
    },
    onError: (error, item) => {
      message.error(
        intl.formatMessage({
          id: label ? 'labels-edit-label-failed' : 'labels-create-label-failed',
        })
      );
      appLog('CreateEditLabelModal -> createLabel -> item', item);
      appLog('CreateEditLabelModal -> createLabel -> err', error);
    },
  });

  const [doEditLabel, { isLoading: isLoadingEdit }] = useMutation(
    editGroupLabel,
    {
      onSuccess: (label) => {
        // hide modal
        // @ts-ignore-line
        onCancel && onCancel();

        queryCache.invalidateQueries('registrationLabelGroups');

        // reset values
        setNameVal('');
      },
      onError: (error, item) => {
        message.error(
          intl.formatMessage({
            id: label
              ? 'labels-edit-label-failed'
              : 'labels-create-label-failed',
          })
        );
        appLog('CreateEditLabelModal -> editLabel -> item', item);
        appLog('CreateEditLabelModal -> editLabel -> err', error);
      },
    }
  );

  useEffect(() => {
    if (modalProps.visible) {
      setTimeout(() => nameInputRef.current?.focus(), 350);

      // fetch folders if data is older than 1min
      if (Date.now() - registrationLabelGroups.updatedAt > 60 * 1000) {
        registrationLabelGroups.refetch();
      }
    }
  }, [modalProps.visible]);

  const isValid = () => {
    if (!nameVal) return false;
    if (!group) return false;
    // check if tag with the same name exists
    return !labelsQuery
      ?.find((g) => g.id === group?.id)
      ?.labels.some((l) => l.title.trim() === nameVal.trim());
  };

  const handleSubmit = async () => {
    // if (!isValid()) return;

    if (label) {
      doEditLabel({
        projectId: projectId!,
        groupId: group!.id,
        labelId: label.id,
        data: {
          title: nameVal,
          order: label.order,
        },
      });
    } else {
      doCreateLabel({
        projectId: projectId!,
        groupId: group!.id,
        data: [{ title: nameVal, order: 0 }],
      });
    }
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({
            id: label ? 'labels-edit-title' : 'labels-create-title',
          })}
          icon={label ? ['far', 'circle-plus'] : ['far', 'square-plus']}
          // don't see ['fas', 'square-plus']
        />
      }
      okText={intl.formatMessage({ id: 'save' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width={420}
      centered
      okButtonProps={{
        disabled: !isValid(),
        loading: isLoading || isLoadingEdit,
      }}
      onOk={handleSubmit}
      onCancel={onCancel}
      {...modalProps}>
      <Input
        ref={nameInputRef}
        placeholder={intl.formatMessage({ id: 'name' })}
        allowClear
        autoCapitalize="words"
        value={nameVal}
        maxLength={255}
        onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
        onChange={({ target: { value } }) =>
          setNameVal(value.charAt(0).toUpperCase() + value.slice(1))
        }
      />
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({});
